import { useSession } from "@store/session";
import { queryOptions, useQuery } from "@tanstack/react-query";
import type { AuthenticatedRequest, Company } from "@types";
import { isCancelledRequestError } from "@utils";
import {
  type AuthenticatedClientFactory,
  createAuthenticatedClient,
} from "./apiClient";
import { DISABLED_KEY, queryKeys } from "./queryKeys";

type CompaniesByIdRequest = AuthenticatedRequest & {
  // Nullable: "this thing has no company" is a real answer a caller can pass,
  // and it must stay distinguishable from "not specified".
  companyId?: string | null;
  /**
   * React Query's per-fetch abort signal. Required, not optional: `companyId`
   * is in this query's key, so a company switch supersedes an in-flight
   * request. Without a signal it is uncancellable and dies at the network
   * layer as a reported `ERR_NETWORK` instead of a clean abort.
   */
  signal: AbortSignal;
};

export const fetchCompaniesById = async (
  { companyId, accessToken, signal }: CompaniesByIdRequest,
  createClient: AuthenticatedClientFactory<"get"> = createAuthenticatedClient,
) => {
  if (!companyId) {
    return [];
  }
  try {
    if (!accessToken) throw new Error("Access token is required");
    const response = await createClient(accessToken).get(
      `/companies/${companyId}`,
      {
        signal,
        timeout: 0,
      },
    );
    return response.data;
  } catch (error) {
    // Re-throw a cancellation unchanged. Flattening it into a generic Error
    // would hide the abort from React Query, which treats a cancelled fetch
    // differently from a failed one.
    if (isCancelledRequestError(error)) throw error;
    throw new Error("Failed to fetch companies");
  }
};

/**
 * Query options factory for company data.
 * Used by route loaders to prefetch company data before component mount.
 */
export const companyQueryOptions = (companyId: string, accessToken: string) =>
  queryOptions<Company>({
    queryKey: queryKeys.company(companyId).details(),
    queryFn: ({ signal }) =>
      fetchCompaniesById({ accessToken, companyId, signal }),
  });

/**
 * The session's company by default. Pass `companyId` to read a DIFFERENT
 * company — an account can own numbers for several, so a surface keyed off one
 * of those numbers must ask for that number's company rather than whichever one
 * the session happens to be viewing.
 */
export const useCompaniesById = (companyIdOverride?: string | null) => {
  const { accessToken, companyId: sessionCompanyId } = useSession();
  // `undefined` is "not specified — use the session's". An explicit `null` is
  // "there is no company", which must NOT quietly become the session's: the
  // caller asked about a specific thing and the answer is that it has none.
  const companyId =
    companyIdOverride === undefined ? sessionCompanyId : companyIdOverride;
  const query = useQuery<Company>({
    queryKey: companyId ? queryKeys.company(companyId).details() : DISABLED_KEY,
    enabled: !!companyId && !!accessToken,
    queryFn: ({ signal }) =>
      fetchCompaniesById({ accessToken, companyId, signal }),
  });

  return query;
};
